import * as marked from "marked";
import { fonts, fontSizes } from "../constants";
import { Block } from "./../models/Block";
import { TextNode } from "./../models/TextNode";
import { Modifier } from "./../types";

function tagToStyle(htmlElement) {
	switch (htmlElement.nodeName) {
		case "B":
		case "STRONG":
			return {
				[Modifier.bold]: true,
			};
		case "I":
		case "EM":
			return {
				[Modifier.italic]: true,
			};
		case "U":
			return {
				[Modifier.underline]: true,
			};
		case "STRIKE":
		case "S":
			return {
				[Modifier.strike]: true,
			};
		case "A":
			return {
				[Modifier.link]: htmlElement.href,
			};
		default:
			return {};
	}
}

function parseTextNodes(htmlElement: HTMLElement, textNodes: TextNode[], style = {}) {
	if (htmlElement.nodeType === htmlElement.TEXT_NODE) {
		const text = htmlElement.textContent
			.replace(/&nbsp;/g, " ")
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">");
		if (text.replace(/\n/g, "").length === 0) {
			// ignore empty nodes: TODO fix
			return;
		}
		const textNode = new TextNode(text, style);
		textNodes.push(textNode);
		return;
	}

	const elementStyle = {};
	const {
		fontSize,
		fontWeight,
		fontFamily,
		color,
		background,
		backgroundColor,
		textDecoration,
		fontStyle,
	} = htmlElement.style || ({} as any);

	const tagStyle = tagToStyle(htmlElement);

	if (fontSize) {
		elementStyle[Modifier.fontSize] = fontSizes[fontSize] ? fontSize : "14px";
	}
	if (fontWeight) {
		elementStyle[Modifier.bold] = fontWeight === "bold" || parseInt(fontWeight, 10) >= 600;
	}
	if (fontFamily) {
		elementStyle[Modifier.fontFamily] = fonts[fontFamily] ? fontFamily : "Roboto";
	}
	if (color) {
		elementStyle[Modifier.color] = color;
	}
	if (background || backgroundColor) {
		elementStyle[Modifier.background] = background || backgroundColor;
	}
	if (textDecoration === "underline") {
		elementStyle[Modifier.underline] = true;
	}
	if (textDecoration === "line-through") {
		elementStyle[Modifier.strike] = true;
	}
	if (fontStyle) {
		elementStyle[Modifier.italic] = fontStyle === "italic";
	}

	const fullStyle = { ...style, ...tagStyle, ...elementStyle };

	const childs = htmlElement.childNodes;
	for (let i = 0; i < childs.length; i++) {
		parseTextNodes(childs[i] as HTMLElement, textNodes, fullStyle);
	}
}

const blockTags = {
	P: "p",
	H1: "h1",
	H2: "h2",
	H3: "h3",
	H4: "h4",
	H5: "h5",
	H6: "h6",
};

function getBlockStyle(htmlElement: HTMLElement) {
	if (!htmlElement || !htmlElement.style) {
		return {};
	}
	const style = {};
	if (htmlElement.style.textAlign) {
		style[Modifier.align] = htmlElement.style.textAlign;
	}
	if (htmlElement.nodeName === "BLOCKQUOTE") {
		style[Modifier.blockquote] = true;
	}

	return style;
}

function parseBlocks(htmlElement: HTMLElement, blocks: Block[], style = {}) {
	style = { ...style, ...getBlockStyle(htmlElement) };

	if (blockTags[htmlElement.nodeName]) {
		const textNodes = [];
		parseTextNodes(htmlElement, textNodes);
		const block = new Block(textNodes, {
			...style,
			[Modifier.style]: blockTags[htmlElement.nodeName],
		});
		blocks.push(block);
		return;
	}

	const childs = htmlElement.childNodes;
	for (let i = 0; i < childs.length; i++) {
		parseBlocks(childs[i] as HTMLElement, blocks, style);
	}
}

export function parser(str: string, mode: "html" | "markdown" = "html") {
	if (mode === "markdown") {
		str = marked(str);
	}
	const htmlElement = document.createElement("div");
	htmlElement.innerHTML = str.replace(/<br\s*[/]?>/g, "\n");
	const blocks = [];
	parseBlocks(htmlElement, blocks);

	if (blocks.length === 0) {
		const style = {
			[Modifier.style]: blockTags[htmlElement.nodeName] || "p",
			...getBlockStyle(htmlElement),
		};

		const textNodes = [];
		parseTextNodes(htmlElement, textNodes);
		const block = new Block(textNodes, style);
		return [block];
	}
	return blocks;
}
